/**
 * One kind of location file: plan or actuals. Download, preview, then commit.
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import api from '@/services/api';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { parseLoadWorkbook, type ParsedLoadRow } from '@/pages/business-performance/parseLoadWorkbook';
import type { LoadKind, PreviewRow } from './types';

interface KpiLoadWidgetProps {
  kind: LoadKind;
  t: (key: string) => string;
  onCommitted: () => void;
}

export const KpiLoadWidget: React.FC<KpiLoadWidgetProps> = ({ kind, t, onCommitted }) => {
  const [file, setFile] = useState<File | null>(null);
  const [sourceRows, setSourceRows] = useState<ParsedLoadRow[]>([]);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const validationErrors = previewData.flatMap((row) =>
    (row.errors || []).map((message) => ({ row: row.row, message }))
  );
  const title = kind === 'target' ? t('kpi.loadKindPlan') : t('kpi.loadKindActuals');

  const reset = () => {
    setFile(null);
    setSourceRows([]);
    setPreviewData([]);
  };

  const downloadTemplate = async () => {
    try {
      setDownloading(true);
      const response = await api.get('/business-performance/loads/template', {
        params: { kind },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = kind === 'target' ? 'tmos-plan-upload.xlsx' : 'tmos-actuals-upload.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('kpi.loadDownloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const previewFile = async (selected: File) => {
    try {
      setProcessing(true);
      const rows = await parseLoadWorkbook(selected);
      setSourceRows(rows);
      const response = await api.post('/business-performance/loads/preview', { kind, rows });
      const preview = (response.data?.data || []) as PreviewRow[];
      setPreviewData(preview);
      if (preview.some((row) => (row.errors || []).length > 0)) toast.error(t('kpi.loadHasErrors'));
      else if (preview.length === 0) toast.error(t('kpi.loadNoRows'));
      else toast.success(t('kpi.loadPreviewReady'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('businessPerformance.errorParsingFile');
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpload = async () => {
    if (previewData.length === 0 || validationErrors.length > 0) {
      toast.error(t('kpi.loadHasErrors'));
      return;
    }
    try {
      setUploading(true);
      const result = await api.post('/business-performance/loads/commit', {
        kind,
        rows: sourceRows,
        file_name: file?.name || null
      });
      const written = result.data?.data?.written as number | undefined;
      const errors = (result.data?.data?.errors || []) as PreviewRow[];
      if (errors.length > 0) {
        setPreviewData(errors);
        toast.error(t('kpi.loadHasErrors'));
        return;
      }
      toast.success(
        written != null
          ? t('kpi.loadCommittedCount').replace('{count}', String(written))
          : t('kpi.loadCommitted')
      );
      reset();
      onCommitted();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('businessPerformance.errorUploading');
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card variant="outline">
      <CardContent className="p-3">
        <h2 className="text-sm font-semibold text-[var(--color-surface-text)] mb-1">{title}</h2>
        <p className="text-[10px] text-[var(--color-secondary-text)] mb-2">
          {kind === 'target' ? t('kpi.loadPlanWidgetHint') : t('kpi.loadActualWidgetHint')}
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Button
            variant="outline"
            onClick={() => {
              void downloadTemplate();
            }}
            disabled={downloading}
            className="flex items-center gap-1.5 text-xs py-1 px-2"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {downloading ? t('common.loading') : t('kpi.loadDownloadTemplate')}
          </Button>
          <label className="inline-flex items-center gap-1.5 px-2 py-1 bg-[var(--color-info)] text-white rounded-md cursor-pointer text-xs">
            <DocumentArrowUpIcon className="w-4 h-4" />
            {t('kpi.loadSelectFile')}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(event) => {
                const selected = event.target.files?.[0];
                if (!selected) return;
                setFile(selected);
                setPreviewData([]);
                void previewFile(selected);
              }}
            />
          </label>
          {file && <span className="text-[10px] text-[var(--color-secondary-text)]">{file.name}</span>}
        </div>
        {processing && (
          <p className="text-xs text-[var(--color-secondary-text)]">{t('kpi.loadProcessing')}</p>
        )}
        {validationErrors.length > 0 && (
          <div className="mb-2">
            <div className="flex items-center gap-1.5 mb-1">
              <ExclamationTriangleIcon className="w-4 h-4 text-[var(--color-error)]" />
              <p className="text-xs font-medium text-[var(--color-surface-text)]">
                {t('kpi.loadHasErrors')} ({validationErrors.length})
              </p>
            </div>
            <div className="max-h-24 overflow-y-auto text-[10px]">
              {validationErrors.map((error, index) => (
                <p key={`${error.row}-${index}`}>
                  {t('kpi.loadRow')} {error.row}: {error.message}
                </p>
              ))}
            </div>
          </div>
        )}
        {previewData.length > 0 && (
          <>
            <div className="overflow-x-auto max-h-40 mb-2 border border-[var(--color-border)] rounded">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 bg-[var(--color-secondary-bg)]">
                  <tr>
                    <th className="px-2 py-1 text-left">{t('kpi.kpiName')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.periodTo')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.value')}</th>
                    {kind === 'target' && <th className="px-2 py-1 text-left">{t('kpi.useType')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row) => (
                    <tr key={row.row} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1">{row.kpi_name}</td>
                      <td className="px-2 py-1">{row.period_to || row.period_from || '—'}</td>
                      <td className="px-2 py-1">{row.value}</td>
                      {kind === 'target' && <td className="px-2 py-1">{row.use_type || '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-1.5">
              <Button variant="outline" onClick={reset} className="text-xs py-1 px-2">
                {t('common.clear')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void handleUpload();
                }}
                disabled={uploading || validationErrors.length > 0}
                className="flex items-center gap-1.5 text-xs py-1 px-2"
              >
                {uploading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                {uploading ? t('kpi.loadSaving') : t('kpi.loadSave')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
